import { Ellipsis, ExternalLink, X } from "lucide-react";
import { Dialog } from "radix-ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router";

import { BOTTOM_NAV_GROUPS, groupOfPath, type VisibleGroup } from "@/app/nav";
import { NavBadge } from "@/app/navBadges";
import { useAuth } from "@/auth/use-auth";
import { cn } from "@/lib/utils";

import { LanguageSelect } from "./language-select";

/** Height reserved at the bottom of the page for the bar (toasts sit above it). */
export const BOTTOM_NAV_HEIGHT = "4.5rem";

/**
 * Tablet/phone bottom bar (T11-01): up to four groups (Home, Orders, Products, Stock — when the
 * role has them) + "More" with the whole menu. Every button is ≥ 56 × 56 px.
 */
export function BottomNav({ groups }: { groups: readonly VisibleGroup[] }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const current = groupOfPath(pathname);
  const main = BOTTOM_NAV_GROUPS.map((key) => groups.find((g) => g.key === key)).filter(
    (g): g is VisibleGroup => g !== undefined,
  );
  const inMore = !main.some((g) => g.key === current);

  return (
    <>
      <nav
        aria-label={t("nav.quick")}
        data-slot="bottom-nav"
        className="fixed inset-x-0 bottom-0 z-40 border-t bg-background pb-[env(safe-area-inset-bottom)]"
      >
        <ul className="mx-auto flex max-w-3xl items-stretch justify-around">
          {main.map((group) => {
            const Icon = group.icon;
            return (
              <li key={group.key} className="flex-1">
                <NavLink
                  to={group.to}
                  end={group.key === "home"}
                  aria-current={current === group.key ? "page" : undefined}
                  className={cn(
                    "flex min-h-[56px] min-w-[56px] flex-col items-center justify-center gap-0.5 px-1 py-2 text-xs",
                    current === group.key ? "font-bold" : "text-muted-foreground",
                  )}
                  data-bottom-nav-item={group.key}
                >
                  <span className="relative">
                    <Icon className="size-6" aria-hidden />
                    <NavBadge
                      target={`group:${group.key}`}
                      className="absolute -top-2 -right-3 inline-flex min-w-5 items-center justify-center rounded-full bg-[#b91c1c] px-1 text-[0.65rem] leading-5 font-bold text-white"
                    />
                  </span>
                  <span className="max-w-full truncate">{t(`nav.groups.${group.key}`)}</span>
                </NavLink>
              </li>
            );
          })}
          <li className="flex-1">
            <button
              type="button"
              onClick={() => {
                setOpen(true);
              }}
              aria-haspopup="dialog"
              aria-expanded={open}
              data-bottom-nav-item="more"
              className={cn(
                "flex min-h-[56px] w-full min-w-[56px] flex-col items-center justify-center gap-0.5 px-1 py-2 text-xs",
                inMore ? "font-bold" : "text-muted-foreground",
              )}
            >
              <Ellipsis className="size-6" aria-hidden />
              <span>{t("nav.more")}</span>
            </button>
          </li>
        </ul>
      </nav>
      <MoreMenu groups={groups} open={open} onOpenChange={setOpen} />
    </>
  );
}

interface MoreMenuProps {
  groups: readonly VisibleGroup[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The whole menu as a sheet (phones/tablets). */
export function MoreMenu({ groups, open, onOpenChange }: MoreMenuProps) {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const close = () => {
    onOpenChange(false);
  };
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          data-slot="more-menu"
          className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85dvh] flex-col rounded-t-xl bg-background shadow-lg"
        >
          <div className="flex items-center justify-between border-b px-4 py-2">
            <Dialog.Title className="text-lg font-bold">{t("nav.menu")}</Dialog.Title>
            <Dialog.Close
              aria-label={t("nav.close_menu")}
              className="inline-flex size-[48px] items-center justify-center rounded-md hover:bg-accent"
            >
              <X className="size-5" aria-hidden />
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">{t("header.open_menu")}</Dialog.Description>
          <nav aria-label={t("nav.main")} className="overflow-y-auto px-4 py-3">
            <ul className="grid gap-4 sm:grid-cols-2">
              {groups.map((group) => {
                const Icon = group.icon;
                return (
                  <li key={group.key}>
                    <p className="mb-1 flex items-center gap-2 font-semibold">
                      <Icon className="size-5" aria-hidden />
                      {t(`nav.groups.${group.key}`)}
                    </p>
                    <ul className="flex flex-col">
                      {group.items.map((item) => (
                        <li key={item.key}>
                          {item.external ? (
                            <a
                              href={item.to}
                              className="flex min-h-[48px] items-center gap-2 rounded-md px-3 hover:bg-accent"
                            >
                              {t(`nav.items.${item.key}`)}
                              <ExternalLink className="size-4" aria-hidden />
                              <span className="sr-only">{t("nav.external")}</span>
                            </a>
                          ) : (
                            <NavLink
                              to={item.to}
                              end={item.end}
                              onClick={close}
                              className={({ isActive }) =>
                                cn(
                                  "flex min-h-[48px] items-center gap-2 rounded-md px-3",
                                  isActive ? "bg-foreground text-background" : "hover:bg-accent",
                                )
                              }
                            >
                              {t(`nav.items.${item.key}`)}
                              {item.comingSoon ? (
                                <span className="ml-auto rounded-sm border px-1 text-[0.65rem]">
                                  {t("nav.coming_soon")}
                                </span>
                              ) : null}
                            </NavLink>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
            <div className="mt-4 flex flex-wrap items-center gap-3 border-t pt-3">
              <NavLink
                to="/profile"
                onClick={close}
                className="flex min-h-[48px] items-center px-3 underline-offset-4 hover:underline"
              >
                {t("nav.items.profile")}
              </NavLink>
              <NavLink
                to="/help"
                onClick={close}
                className="flex min-h-[48px] items-center px-3 underline-offset-4 hover:underline"
              >
                {t("nav.items.help")}
              </NavLink>
              <LanguageSelect />
              <button
                type="button"
                className="ml-auto min-h-[48px] rounded-md border px-4"
                onClick={() => {
                  close();
                  void signOut();
                }}
              >
                {t("auth.sign_out")}
              </button>
            </div>
          </nav>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
